//@ts-nocheck
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowLeftOutlinedIcon from '@mui/icons-material/KeyboardArrowLeftOutlined';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import PollOutlinedIcon from '@mui/icons-material/PollOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import {
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Divider,
  Popper,
  Box,
  Grow,
  Fade,
  Paper,
  Backdrop,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetMyPersonalization, useGetMyViewDetails } from 'services/v1/Tenant/ConfigurationService';
import { MyViewDashboardItem, QuickAccessItem } from 'types/api/Tenant/ConfigurationTypes';
import { getQuickAccessNavigateURL } from 'components/LayoutComponent/VerticalLayout/QuickAccess';
import { PAGE_REF_NAVIGATION } from 'utils/Animation';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { useDashboardNaviagationPrefetchHandler } from 'hooks/useDashboardNavigationPrefetchHandler';

// ── Color Palette ─────────────────────────────────────────────────────
const ACCENT_GREEN = '#42BB93';
const ACCENT_BLUE = '#3B4797';
const TEXT_SECONDARY = '#98A2AE';
const TEXT_MUTED = '#737373';
const TEXT_DARK = '#333';

// ── Types & Enums ─────────────────────────────────────────────────────
export enum ControllerNavigationDirection {
  UP = 'up',
  DOWN = 'down',
  LEFT = 'left',
  RIGHT = 'right',
  CENTER = 'center',
}

interface ControllerNavigationProps {
  onNavigate: (direction: ControllerNavigationDirection) => void;
  onPrefetch?: (direction: ControllerNavigationDirection) => void;
  enableNavigations?: ControllerNavigationDirection[];
  tooltips?: {
    [key in ControllerNavigationDirection]?: string;
  };
}

// ── Helpers ───────────────────────────────────────────────────────────
const ARROW_ICONS = {
  [ControllerNavigationDirection.UP]: KeyboardArrowUpOutlinedIcon,
  [ControllerNavigationDirection.DOWN]: KeyboardArrowDownOutlinedIcon,
  [ControllerNavigationDirection.LEFT]: KeyboardArrowLeftOutlinedIcon,
  [ControllerNavigationDirection.RIGHT]: KeyboardArrowRightOutlinedIcon,
  [ControllerNavigationDirection.CENTER]: GridViewRoundedIcon,
};

const TOOLTIP_PLACEMENT_MAP = {
  [ControllerNavigationDirection.UP]: 'top',
  [ControllerNavigationDirection.DOWN]: 'bottom',
  [ControllerNavigationDirection.LEFT]: 'left',
  [ControllerNavigationDirection.RIGHT]: 'right',
  [ControllerNavigationDirection.CENTER]: 'bottom',
} as const;

function getComponentTypeIcon(type?: string) {
  const sx = { fontSize: 18, color: ACCENT_GREEN };
  switch (type) {
    case 'INSIGHT_VIEW':
      return <InsightsOutlinedIcon sx={sx} />;
    case 'CHART':
      return <BarChartOutlinedIcon sx={sx} />;
    case 'CUBE':
      return <WidgetsOutlinedIcon sx={sx} />;
    case 'K_TREE':
      return <AccountTreeOutlinedIcon sx={sx} />;
    case 'SUBJECT':
      return <DescriptionOutlinedIcon sx={sx} />;
    case 'SUBJECT_PROCEDURE':
      return <FactCheckOutlinedIcon sx={sx} />;
    case 'EVENT':
      return <EventOutlinedIcon sx={sx} />;
    case 'DASHBOARD':
      return <DashboardOutlinedIcon sx={sx} />;
    case 'TABLE_ANALYTIC':
      return <TableChartOutlinedIcon sx={sx} />;
    case 'STANDARD_VIEW':
      return <PollOutlinedIcon sx={sx} />;
    default:
      return <InsightsOutlinedIcon sx={sx} />;
  }
}

// ── NavigationPill (arrow-only with tooltip) ─────────────────────────
interface NavigationPillProps {
  direction: ControllerNavigationDirection;
  tooltip: string;
  onClick: () => void;
  onMouseEnter: () => void;
}

function NavigationPill({ direction, tooltip, onClick, onMouseEnter }: Readonly<NavigationPillProps>) {
  const ArrowIcon = ARROW_ICONS[direction];

  const button = (
    <IconButton
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      sx={{
        p: '2px',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: 'rgba(66, 187, 147, 0.08)',
          '& .MuiSvgIcon-root': { color: ACCENT_GREEN },
        },
        '&:active': {
          transform: 'scale(0.9)',
        },
      }}
    >
      <ArrowIcon sx={{ fontSize: 20, color: TEXT_MUTED, transition: 'color 0.2s ease' }} />
    </IconButton>
  );

  if (!tooltip) return button;

  return (
    <Tooltip
      title={tooltip}
      placement={TOOLTIP_PLACEMENT_MAP[direction]}
      disableInteractive
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'flip',
              options: { fallbackPlacements: ['top', 'bottom'] },
            },
          ],
          sx: { pointerEvents: 'none' },
        },
      }}
    >
      {button}
    </Tooltip>
  );
}

// ── ViewCard ──────────────────────────────────────────────────────────
interface ViewCardProps {
  item: QuickAccessItem;
  isActive: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
}

function ViewCard({ item, isActive, onClick, onMouseEnter }: Readonly<ViewCardProps>) {
  return (
    <Box
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        cursor: 'pointer',
        borderLeft: `3px solid ${isActive ? ACCENT_GREEN : 'transparent'}`,
        backgroundColor: isActive ? 'rgba(66, 187, 147, 0.06)' : 'transparent',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: isActive ? 'rgba(66, 187, 147, 0.1)' : 'rgba(0, 0, 0, 0.03)',
          transform: 'translateX(2px)',
        },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isActive ? 'rgba(66, 187, 147, 0.12)' : '#f0f4f8',
          flexShrink: 0,
          transition: 'background-color 0.2s ease',
        }}
      >
        {getComponentTypeIcon(item.componentType)}
      </Box>
      <Stack sx={{ overflow: 'hidden', minWidth: 0, flex: 1 }}>
        <Typography
          noWrap
          sx={{
            fontSize: 13,
            fontWeight: isActive ? 600 : 500,
            color: isActive ? ACCENT_GREEN : TEXT_DARK,
            lineHeight: 1.3,
          }}
        >
          {item.title}
        </Typography>
        {item.secondaryTitle && (
          <Typography
            noWrap
            sx={{
              fontSize: 11,
              color: TEXT_SECONDARY,
              lineHeight: 1.3,
            }}
          >
            {item.secondaryTitle}
          </Typography>
        )}
      </Stack>
      {isActive && (
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: ACCENT_GREEN,
            flexShrink: 0,
            ml: 'auto',
          }}
        />
      )}
    </Box>
  );
}

// ── Main Component ────────────────────────────────────────────────────
export function ControllerNavigation(props: ControllerNavigationProps) {
  const {
    onNavigate,
    onPrefetch,
    tooltips = {},
    enableNavigations = [
      ControllerNavigationDirection.UP,
      ControllerNavigationDirection.DOWN,
      ControllerNavigationDirection.LEFT,
      ControllerNavigationDirection.RIGHT,
    ],
  } = props;
  const { role } = useSelector((state: RootState) => state.auth);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [searchParams] = useSearchParams();
  const prefetchHandler = useDashboardNaviagationPrefetchHandler();
  const navigate = useNavigate();
  const myViewId = searchParams.get('myViewId');

  const { data: myViewDetails } = useGetMyViewDetails(myViewId);
  const { data: allMyViewsData } = useGetMyPersonalization({ enabled: true });

  const currentDashboard: MyViewDashboardItem | null = useMemo(() => {
    if (allMyViewsData?.data) {
      const selectedDashboardId = myViewDetails?.data.dashboardId;
      const selectedDashboard =
        allMyViewsData.data.dashboards.find((d) => d.id === selectedDashboardId) || null;
      if (selectedDashboard) return selectedDashboard;
    }
    return null;
  }, [allMyViewsData?.data, myViewDetails?.data]);

  const insightViewList: QuickAccessItem[] = useMemo(() => {
    return currentDashboard?.myViews ?? [];
  }, [currentDashboard]);

  const handleOnClickButtonMyView = (myViewItem: QuickAccessItem) => {
    const url = getQuickAccessNavigateURL(myViewItem, undefined, PAGE_REF_NAVIGATION.LEFT, role);
    if (url) navigate(url);
    setAnchorEl(null);
  };

  const handleOnPrefetchMyView = (myViewItem: QuickAccessItem) => {
    prefetchHandler(myViewItem);
  };

  const handleOnPrefetchDirection = (direction: ControllerNavigationDirection) => {
    onPrefetch?.(direction);
  };

  const handleOpenIndex = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleCloseIndex = () => {
    setAnchorEl(null);
  };

  const isEnabled = (dir: ControllerNavigationDirection) => enableNavigations.includes(dir);
  const popperOpen = Boolean(anchorEl);

  return (
    <>
      {/* Backdrop overlay — clickable to close */}
      <Backdrop
        open={popperOpen}
        onClick={handleCloseIndex}
        sx={{ zIndex: 1100, backgroundColor: 'rgba(0, 0, 0, 0.12)' }}
      />

      {/* ── Navigation D-Pad ── */}
      <Stack alignItems='center' spacing={0} sx={{ width: 'fit-content', position: 'relative' }}>
        {/* UP */}
        <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 24 }}>
          {isEnabled(ControllerNavigationDirection.UP) ? (
            <NavigationPill
              direction={ControllerNavigationDirection.UP}
              tooltip={tooltips[ControllerNavigationDirection.UP] ?? ''}
              onClick={() => onNavigate(ControllerNavigationDirection.UP)}
              onMouseEnter={() => handleOnPrefetchDirection(ControllerNavigationDirection.UP)}
            />
          ) : (
            <Box sx={{ height: 24 }} />
          )}
        </Box>

        {/* LEFT — INDEX BUTTON — RIGHT */}
        <Stack direction='row' alignItems='center' justifyContent='space-between' width={70}>
          {isEnabled(ControllerNavigationDirection.LEFT) ? (
            <NavigationPill
              direction={ControllerNavigationDirection.LEFT}
              tooltip={tooltips[ControllerNavigationDirection.LEFT] ?? ''}
              onClick={() => onNavigate(ControllerNavigationDirection.LEFT)}
              onMouseEnter={() => handleOnPrefetchDirection(ControllerNavigationDirection.LEFT)}
            />
          ) : (
            <Box sx={{ width: 24 }} />
          )}

          <Tooltip title='Navigation Index'>
            <IconButton
              onClick={handleOpenIndex}
              sx={{
                p: 0.5,
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(59, 71, 151, 0.08)',
                  '& .MuiSvgIcon-root': { color: ACCENT_BLUE },
                },
              }}
            >
              <GridViewRoundedIcon sx={{ fontSize: 18, color: TEXT_SECONDARY, transition: 'color 0.2s ease' }} />
            </IconButton>
          </Tooltip>

          {isEnabled(ControllerNavigationDirection.RIGHT) ? (
            <NavigationPill
              direction={ControllerNavigationDirection.RIGHT}
              tooltip={tooltips[ControllerNavigationDirection.RIGHT] ?? ''}
              onClick={() => onNavigate(ControllerNavigationDirection.RIGHT)}
              onMouseEnter={() => handleOnPrefetchDirection(ControllerNavigationDirection.RIGHT)}
            />
          ) : (
            <Box sx={{ width: 24 }} />
          )}
        </Stack>

        {/* DOWN */}
        <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 24 }}>
          {isEnabled(ControllerNavigationDirection.DOWN) ? (
            <NavigationPill
              direction={ControllerNavigationDirection.DOWN}
              tooltip={tooltips[ControllerNavigationDirection.DOWN] ?? ''}
              onClick={() => onNavigate(ControllerNavigationDirection.DOWN)}
              onMouseEnter={() => handleOnPrefetchDirection(ControllerNavigationDirection.DOWN)}
            />
          ) : (
            <Box sx={{ height: 24 }} />
          )}
        </Box>
      </Stack>

      {/* ── Navigation Index Popup ── */}
      <Popper
        open={popperOpen}
        anchorEl={anchorEl}
        placement='bottom-end'
        sx={{ zIndex: 1200 }}
        modifiers={[
          { name: 'offset', options: { offset: [0, 8] } },
          {
            name: 'preventOverflow',
            enabled: true,
            options: { altAxis: true, altBoundary: true, tether: true, rootBoundary: 'document', padding: 8 },
          },
        ]}
      >
        <Grow in={popperOpen} timeout={250} style={{ transformOrigin: 'top right' }}>
          <Paper
            elevation={8}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              minWidth: 280,
              maxWidth: 340,
              border: '1px solid rgba(0, 0, 0, 0.06)',
            }}
          >
            {/* Header */}
            <Stack sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
              <Stack direction='row' justifyContent='space-between' alignItems='flex-start'>
                <Stack>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: ACCENT_BLUE, lineHeight: 1.3 }}>
                    {currentDashboard?.dashboardName}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY, mt: 0.25 }}>Navigation Index</Typography>
                </Stack>
                <IconButton size='small' onClick={handleCloseIndex} sx={{ mt: -0.5 }}>
                  <HighlightOffRoundedIcon sx={{ color: TEXT_SECONDARY, fontSize: 20 }} />
                </IconButton>
              </Stack>
            </Stack>

            <Divider />

            {/* View Cards */}
            <Stack sx={{ maxHeight: 380, overflow: 'auto', p: 1 }} gap={0.5}>
              {insightViewList.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>No views available</Typography>
                </Box>
              ) : (
                insightViewList.map((myViewItem, index) => (
                  <Fade
                    key={myViewItem.value}
                    in={popperOpen}
                    style={{ transitionDelay: popperOpen ? `${index * 40}ms` : '0ms' }}
                  >
                    <Box>
                      <ViewCard
                        item={myViewItem}
                        isActive={myViewId === myViewItem.viewId}
                        onClick={() => handleOnClickButtonMyView(myViewItem)}
                        onMouseEnter={() => handleOnPrefetchMyView(myViewItem)}
                      />
                    </Box>
                  </Fade>
                ))
              )}
            </Stack>
          </Paper>
        </Grow>
      </Popper>
    </>
  );
}
